// src/hooks/useClientParticipants.js
//
// Pass 85 — loads the PARTICIPANTS explicitly linked to a commercial client, for the operator CRM. Org-scoped at the
// QUERY (organizationId + clientId — matches the clientParticipantLinks read rule, not an in-memory filter). Read-only.
import { useEffect, useState } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { PLATFORM_COLLECTIONS } from '../utils/platformSchema';

export function useClientParticipants(db, organizationId, clientId, reloadKey = 0) {
  const [state, setState] = useState({ loading: true, error: '', participants: [] });
  useEffect(() => {
    if (!db || !organizationId || !clientId) { setState({ loading: false, error: '', participants: [] }); return undefined; }
    let cancelled = false;
    (async () => {
      setState((s) => ({ ...s, loading: true, error: '' }));
      try {
        const linksSnap = await getDocs(query(
          collection(db, PLATFORM_COLLECTIONS.CLIENT_PARTICIPANT_LINKS),
          where('organizationId', '==', organizationId),
          where('clientId', '==', clientId),
        ));
        const links = linksSnap.docs.map((d) => d.data());
        const profiles = await Promise.all(links.map(async (l) => {
          try {
            const p = await getDoc(doc(db, PLATFORM_COLLECTIONS.PARTICIPANT_PROFILES, String(l.participantId)));
            return p.exists() ? { ...p.data(), participantId: String(l.participantId) } : { participantId: String(l.participantId), missing: true };
          } catch { return null; }
        }));
        if (!cancelled) setState({ loading: false, error: '', participants: profiles.filter(Boolean) });
      } catch (e) {
        // FAIL-CLOSED: a read error is NOT an empty result (Motus §58).
        if (!cancelled) setState({ loading: false, error: e?.message || 'Could not load participants.', participants: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [db, organizationId, clientId, reloadKey]);
  return state;
}

export default useClientParticipants;
